import React, { useEffect, useRef, useState } from 'react';
import {
  BUG_REPORT_EVENT, BUG_TEXT_MIN, BUG_TEXT_MAX,
  installBugReportErrorTap, submitBugReport,
} from '../utils/bugReport';

// 🐛 Bug report modal v1 (09-20). Mounted ONCE in App.js beside LofiRadio /
// CaptureTray (same embed/theater exclusions). Renders NOTHING until
// something calls openBugReport(), so it costs zero chrome: no floating
// button over the room art. Doors today: `/bug` in the composer, and the
// "Report a bug" button in the What's New pill.
//
// ⚠️ This frontend has NO global `box-sizing: border-box`: every width:100%
// + padding element below sets it, or it overflows a phone.
// Copy rules: short, punchy, no em dashes.

installBugReportErrorTap();

const field = {
  width: '100%', boxSizing: 'border-box', borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)',
  color: '#f4efe6', padding: '10px 12px', fontSize: 16, // ≥16px: no iOS zoom
  fontFamily: 'inherit', outline: 'none',
};

export default function BugReportModal() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [contact, setContact] = useState('');
  const [source, setSource] = useState(null);
  const [state, setState] = useState('idle'); // idle | sending | sent
  const [error, setError] = useState(null);
  const sendingRef = useRef(false); // synchronous: a double tap must not file twice
  const boxRef = useRef(null);

  useEffect(() => {
    const onOpen = (e) => {
      const d = (e && e.detail) || {};
      setText(typeof d.text === 'string' ? d.text.slice(0, BUG_TEXT_MAX) : '');
      setSource(d.source || null);
      setState('idle');
      setError(null);
      setOpen(true);
    };
    window.addEventListener(BUG_REPORT_EVENT, onOpen);
    return () => window.removeEventListener(BUG_REPORT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { if (boxRef.current) boxRef.current.focus(); }, 50);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!open) return null;

  const trimmed = text.trim();
  const canSend = state === 'idle' && trimmed.length >= BUG_TEXT_MIN && trimmed.length <= BUG_TEXT_MAX;

  const send = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (sendingRef.current || !canSend) return;
    sendingRef.current = true;
    setState('sending');
    setError(null);
    try {
      await submitBugReport({ text: trimmed, contact: contact.trim() || null, source });
      setState('sent');
      setText('');
    } catch (err) {
      setError(err.message || 'That did not send. Try again in a moment.');
      setState('idle');
    } finally {
      sendingRef.current = false;
    }
  };

  return (
    <div
      data-testid="bug-report-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, boxSizing: 'border-box',
      }}
    >
      <form
        onSubmit={send}
        role="dialog"
        aria-modal="true"
        aria-label="Report a bug"
        style={{
          width: 'min(460px, 100%)', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box',
          background: '#221d1a', color: '#f4efe6', borderRadius: 16, padding: 18,
          border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 18px 60px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>🐛 Report a bug</div>
          <button type="button" onClick={() => setOpen(false)} aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#f4efe6', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
        </div>

        {state === 'sent' ? (
          <>
            <div style={{ fontSize: 15, lineHeight: 1.45 }}>Got it. Thank you! A human reads every one of these.</div>
            <button type="button" onClick={() => setOpen(false)}
              style={{ ...field, cursor: 'pointer', fontWeight: 700, background: '#c9976b', color: '#221d1a', border: 'none' }}>Done</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, opacity: 0.8, lineHeight: 1.4 }}>
              What happened, and what did you expect? We attach the page, your browser, screen size and recent errors. Never your chat.
            </div>
            <textarea
              ref={boxRef}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, BUG_TEXT_MAX))}
              placeholder="My message showed up twice after I hit Enter..."
              rows={5}
              style={{ ...field, resize: 'vertical', minHeight: 110 }}
            />
            <input
              type="email"
              value={contact}
              onChange={(e) => setContact(e.target.value.slice(0, 120))}
              placeholder="Email, only if you want a reply (optional)"
              style={field}
            />
            {error && <div role="alert" style={{ color: '#ffb4a2', fontSize: 14 }}>{error}</div>}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, opacity: 0.6 }}>{trimmed.length}/{BUG_TEXT_MAX}</span>
              <button type="submit" disabled={!canSend}
                style={{
                  borderRadius: 10, border: 'none', padding: '10px 18px', fontSize: 16, fontWeight: 700,
                  background: '#c9976b', color: '#221d1a', cursor: canSend ? 'pointer' : 'not-allowed', opacity: canSend ? 1 : 0.5,
                }}>
                {state === 'sending' ? 'Sending...' : 'Send report'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
